import { Empty, List, Skeleton } from 'antd'
import { BadgeIndicator } from 'sutra/components/BadgeIndicator/BadgeIndicator'
import { Button } from 'sutra/components/Button'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { NotificationRow } from 'features/notifications/components/notification-row/NotificationRow'
import {
  BellButton,
  BellIcon,
  EmptyWrapper,
  ErrorIcon,
  ErrorState,
  LoadMoreRow,
  Panel,
  PanelBody,
  PanelPopover,
  SkeletonWrapper,
  TooltipAnchor,
} from 'features/notifications/components/notifications-panel-view/styled'
import { NotificationsPanelHeader } from 'features/notifications/components/NotificationsPanelHeader/NotificationsPanelHeader'
import type { NotificationsPanelViewProps } from 'features/notifications/types'

/**
 * Pure-render notifications bell + dropdown panel.
 *
 * The view does NOT call service hooks, store hooks, or react-query —
 * the container wires those up and passes everything in as props. Per
 * `view-component-purity`, this file must stay free of `use*` data
 * imports.
 */
export const NotificationsPanelView = ({
  notifications,
  unreadCount,
  isLoading,
  isError,
  onlyUnread,
  hasMore,
  isLoadingMore,
  open,
  selectedIds,
  onOpenChange,
  onOnlyUnreadChange,
  onLoadMore,
  onToggleSelect,
  onToggleSelectAll,
  onClearSelection,
  onMarkSelectedRead,
  onMarkSelectedUnread,
  onDeleteSelected,
  onMarkAsRead,
  onRetry,
  onOpenDetail,
}: NotificationsPanelViewProps): JSX.Element => {
  const selectedCount = selectedIds.length
  const hasRows = notifications.length > 0
  const allSelected = hasRows && selectedCount === notifications.length
  // Single read/unread toggle: a mixed selection converges on one state.
  // Any unread row in the selection → the action is "Mark read"; only an
  // all-read selection offers "Mark unread".
  const selectionHasUnread = notifications.some(
    (n) => n.acknowledged_at === null && selectedIds.includes(n.id),
  )
  const panel = (
    <Panel data-testid="notifications-panel">
      <NotificationsPanelHeader
        hasRows={hasRows}
        allSelected={allSelected}
        selectedCount={selectedCount}
        onToggleSelectAll={onToggleSelectAll}
        selectionHasUnread={selectionHasUnread}
        onMarkSelectedRead={onMarkSelectedRead}
        onMarkSelectedUnread={onMarkSelectedUnread}
        onDeleteSelected={onDeleteSelected}
        onClearSelection={onClearSelection}
        unreadCount={unreadCount}
        onlyUnread={onlyUnread}
        onOnlyUnreadChange={onOnlyUnreadChange}
      />
      <PanelBody>
        {isLoading ? (
          <SkeletonWrapper data-testid="notifications-skeleton">
            <Skeleton active paragraph={{ rows: 2 }} />
            <Skeleton active paragraph={{ rows: 2 }} />
            <Skeleton active paragraph={{ rows: 2 }} />
          </SkeletonWrapper>
        ) : isError ? (
          <ErrorState data-testid="notifications-error">
            <ErrorIcon />
            <span>We couldn’t load your notifications.</span>
            <Button variant="secondary" size="xs" onClick={onRetry}>
              Retry
            </Button>
          </ErrorState>
        ) : notifications.length === 0 ? (
          <EmptyWrapper data-testid="notifications-empty">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="You’re all caught up."
            />
          </EmptyWrapper>
        ) : (
          <List
            dataSource={notifications.slice()}
            loadMore={
              hasMore ? (
                <LoadMoreRow>
                  <Button
                    variant="secondary"
                    size="xs"
                    loading={isLoadingMore}
                    onClick={onLoadMore}
                    data-testid="notifications-load-more"
                  >
                    Load more
                  </Button>
                </LoadMoreRow>
              ) : null
            }
            renderItem={(notification) => (
              <NotificationRow
                notification={notification}
                selected={selectedIds.includes(notification.id)}
                onToggleSelect={onToggleSelect}
                onMarkAsRead={onMarkAsRead}
                onOpenDetail={onOpenDetail}
              />
            )}
          />
        )}
      </PanelBody>
    </Panel>
  )

  return (
    <Tooltip content="Notifications">
      <TooltipAnchor>
        <PanelPopover
          open={open}
          onOpenChange={onOpenChange}
          side="bottom"
          align="end"
          aria-label="Notifications"
          content={panel}
        >
          <BellButton
            type="button"
            aria-label="Notifications"
            data-testid="notifications-bell"
          >
            <BadgeIndicator count={unreadCount}>
              <BellIcon />
            </BadgeIndicator>
          </BellButton>
        </PanelPopover>
      </TooltipAnchor>
    </Tooltip>
  )
}
